'use client'

import React, { useCallback, useEffect, useId, useMemo, useRef } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Button, cn, Field, Input, useFieldA11y } from '@kiao-voice/ui-kit'
import { z } from 'zod'
import { isValidTaiwanMobile } from '../../../lib/tw-validation'
import type * as TappayType from './types'
import type { CardFormSubmitPayload } from './card-form'

export type { CardFormSubmitPayload }

interface CardholderState {
  name: string
  email: string
  phoneNumber: string
}

interface Props {
  /** SDK readiness signal from `<TappayScript>`. */
  ready: boolean
  /** Default cardholder values (e.g. the signed-in user's email). */
  initialCardholder?: Partial<CardholderState>
  /** Disable the whole form (parent is mid-request). */
  disabled?: boolean
  /** Called with prime + cardholder when the user submits a valid card. */
  onSubmit: (payload: CardFormSubmitPayload) => void | Promise<void>
  /** Optional override for `window.TPDirect` — useful for vitest. */
  tpDirect?: TappayType.TPDirect
  submitLabel: string
  submittingLabel: string
  /** Small hint rendered under the email field — e.g. guest checkout noting the
   *  cardholder email doubles as the account email. */
  emailHint?: string
}

type HostedField = 'number' | 'expiry' | 'ccv'

interface CardFormValues extends CardholderState {
  /** Validation-only TapPay fields. PCI values remain inside TapPay iframes. */
  number?: undefined
  expiry?: undefined
  ccv?: undefined
}

const initialStatus: TappayType.FieldsStatus['status'] = { number: 1, expiry: 1, ccv: 1 }

const hostedFields: HostedField[] = ['number', 'expiry', 'ccv']

/** TapPay iframe 無法讀我們的 CSS 變數——在 setup 時把 token 解析成實值傳入，
 *  讓 kiao-tokens.css 維持唯一色彩定義來源。 */
export function resolveCssToken(name: string): string {
  if (typeof window === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/* 內嵌欄位容器：沿用 .input 外觀，內部讓 TapPay iframe 填滿 */
const hostedFieldStyle: React.CSSProperties = {
  height: 'var(--ctl-h)',
  paddingTop: 0,
  paddingBottom: 0,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
}

const payGridStyle: React.CSSProperties = { display: 'grid', gap: 'var(--gap-sm)' }
const twoColStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--gap-sm)',
}

function hostedFieldClassName(invalid: boolean): string {
  return cn('input num', invalid && 'border-error outline-2 outline-[var(--error-soft)]')
}

interface HostedCardInputProps {
  id: string
  label: string
  invalid: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  testId: string
}

/**
 * TapPay owns the PCI iframe, but the outer group and iframe still need the
 * same accessible name/error description that ordinary Field controls get.
 */
function HostedCardInput({ id, label, invalid, containerRef, testId }: HostedCardInputProps) {
  const { descriptionId } = useFieldA11y()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const applyA11y = () => {
      for (const frame of container.querySelectorAll('iframe')) {
        frame.title = label
        frame.setAttribute('aria-label', label)
        if (descriptionId) frame.setAttribute('aria-describedby', descriptionId)
        else frame.removeAttribute('aria-describedby')
      }
    }

    applyA11y()
    const observer = new MutationObserver(applyA11y)
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [containerRef, descriptionId, label])

  return (
    <div
      id={id}
      ref={containerRef}
      role="group"
      aria-label={label}
      aria-describedby={descriptionId}
      className={hostedFieldClassName(invalid)}
      style={hostedFieldStyle}
      aria-invalid={invalid || undefined}
      data-testid={testId}
    />
  )
}

/**
 * Prototype-styled TapPay card form (design/app-account.html pay dialog).
 *
 * Card number / expiry / CCV stay TapPay-hosted iframes (PCI: the PAN never
 * touches our JS). Their SDK field status is mapped into the same RHF error
 * store consumed by <Field> as name / email / phone, so every field shares
 * one validation lifecycle (phone REQUIRED, Taiwan mobile: 09 + 8 digits).
 */
export function KiaoCardForm({
  ready,
  initialCardholder,
  disabled,
  onSubmit,
  tpDirect,
  submitLabel,
  submittingLabel,
  emailHint,
}: Props) {
  const { t } = useTranslation('web')
  const formId = useId()
  const numberId = `${formId}-number`
  const expiryId = `${formId}-expiry`
  const ccvId = `${formId}-ccv`
  const nameId = `${formId}-name`
  const emailId = `${formId}-email`
  const phoneId = `${formId}-phone`

  const hostedError = useMemo<Record<HostedField, string>>(
    () => ({
      number: t('billing.errorCardNumber'),
      expiry: t('billing.errorCardExpiry'),
      ccv: t('billing.errorCardCcv'),
    }),
    [t]
  )
  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('billing.errorCardholderName')),
        email: z.string().trim().email(t('billing.errorCardholderEmail')),
        phoneNumber: z.string().refine(isValidTaiwanMobile, t('billing.errorCardholderPhone')),
        // These validation-only fields deliberately never receive card values.
        number: z.undefined().optional(),
        expiry: z.undefined().optional(),
        ccv: z.undefined().optional(),
      }),
    [t]
  )
  const form = useForm<CardFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: initialCardholder?.name ?? '',
      email: initialCardholder?.email ?? '',
      phoneNumber: initialCardholder?.phoneNumber ?? '',
    },
  })
  const { errors, isSubmitting } = form.formState
  const mounted = useRef(false)
  const submittedRef = useRef(false)
  const fieldStatusRef = useRef<TappayType.FieldsStatus['status']>(initialStatus)
  const canGetPrimeRef = useRef(false)
  const numberRef = useRef<HTMLDivElement>(null)
  const expiryRef = useRef<HTMLDivElement>(null)
  const ccvRef = useRef<HTMLDivElement>(null)

  /** TapPay remains the PCI input owner. RHF owns only the corresponding errors. */
  const syncHostedStatus = useCallback(
    (update: TappayType.FieldsStatus) => {
      fieldStatusRef.current = update.status
      canGetPrimeRef.current = update.canGetPrime

      for (const field of hostedFields) {
        const status = update.status[field]
        const invalid = status === 2 || (submittedRef.current && status === 1)
        if (invalid) {
          form.setError(field, { type: 'tappay', message: hostedError[field] })
        } else {
          // TapPay status 3 means the hosted input is being edited. Clear stale
          // submit feedback immediately; TapPay will emit 2 again if it remains invalid.
          form.clearErrors(field)
        }
      }

      if (update.canGetPrime) form.clearErrors('root.card')
    },
    [form, hostedError]
  )
  const syncHostedStatusRef = useRef(syncHostedStatus)
  useEffect(() => {
    syncHostedStatusRef.current = syncHostedStatus
  }, [syncHostedStatus])

  const readHostedStatus = useCallback(
    (direct: TappayType.TPDirect) => {
      const update = direct.card.getTappayFieldsStatus()
      if (update) {
        syncHostedStatus(update)
        return update
      }
      const fallback: TappayType.FieldsStatus = {
        canGetPrime: canGetPrimeRef.current,
        hasError: false,
        cardType: 'unknown',
        status: fieldStatusRef.current,
      }
      syncHostedStatus(fallback)
      return fallback
    },
    [syncHostedStatus]
  )

  // Mount the TapPay iframes exactly once after the SDK is ready.
  useEffect(() => {
    if (!ready || mounted.current) return
    const direct = tpDirect ?? (typeof window !== 'undefined' ? window.TPDirect : undefined)
    if (!direct) return
    if (!numberRef.current || !expiryRef.current || !ccvRef.current) return

    direct.card.setup({
      fields: {
        number: { element: numberRef.current, placeholder: '**** **** **** ****' },
        expirationDate: { element: expiryRef.current, placeholder: 'MM / YY' },
        ccv: { element: ccvRef.current, placeholder: 'CCV' },
      },
      // TapPay's hosted iframes can't read our CSS variables, so the token
      // values are resolved at runtime from kiao-tokens.css — the tokens stay
      // the single source of truth (no mirrored hex literals).
      styles: {
        input: {
          'font-size': '14px',
          color: resolveCssToken('--fg'),
          'background-color': resolveCssToken('--bg'),
          border: '0',
          outline: 'none',
          padding: '0',
          margin: '0',
          width: '100%',
          height: '100%',
          'font-family': "'Inter', -apple-system, system-ui, sans-serif",
        },
        '::placeholder': { color: resolveCssToken('--muted') },
        '.invalid': { color: resolveCssToken('--error') },
      },
    })

    direct.card.onUpdate((update) => {
      syncHostedStatusRef.current(update)
    })

    mounted.current = true
  }, [ready, tpDirect])

  const submitPayment = useCallback(
    async (cardholder: CardFormValues) => {
      const direct = tpDirect ?? (typeof window !== 'undefined' ? window.TPDirect : undefined)
      if (!direct) {
        form.setError('root.card', { type: 'sdk', message: t('billing.errorCardFormSdk') })
        return
      }

      const status = readHostedStatus(direct)
      if (!status.canGetPrime) {
        const hasHostedFieldError = hostedFields.some((field) => status.status[field] !== 0)
        if (!hasHostedFieldError) {
          form.setError('root.card', {
            type: 'incomplete',
            message: t('billing.errorCardFormIncomplete'),
          })
        }
        return
      }

      form.clearErrors('root.card')
      await new Promise<void>((resolve, reject) => {
        let resolved = false
        const timeout = setTimeout(() => {
          if (resolved) return
          resolved = true
          form.setError('root.card', {
            type: 'timeout',
            message: t('billing.errorCardValidationTimeout'),
          })
          resolve()
        }, 10_000)

        direct.card.getPrime(async (result) => {
          if (resolved) return
          resolved = true
          clearTimeout(timeout)
          if (result.status !== 0) {
            form.setError('root.card', {
              type: 'tappay',
              message: t('billing.errorCardValidationFailed', { msg: result.msg }),
            })
            resolve()
            return
          }
          const successResult = result as TappayType.PrimeSuccess
          try {
            await onSubmit({
              prime: successResult.card.prime,
              cardholder: {
                name: cardholder.name.trim(),
                email: cardholder.email.trim(),
                phone_number: cardholder.phoneNumber.trim(),
              },
            })
            resolve()
          } catch (error) {
            reject(error)
          }
        })
      })
    },
    [form, onSubmit, readHostedStatus, t, tpDirect]
  )

  const syncHostedStatusAfterValidation = useCallback(() => {
    const direct = tpDirect ?? (typeof window !== 'undefined' ? window.TPDirect : undefined)
    if (direct) {
      readHostedStatus(direct)
      return
    }

    syncHostedStatus({
      canGetPrime: canGetPrimeRef.current,
      hasError: false,
      cardType: 'unknown',
      status: fieldStatusRef.current,
    })
  }, [readHostedStatus, syncHostedStatus, tpDirect])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (disabled || isSubmitting) {
      event.preventDefault()
      return
    }
    submittedRef.current = true
    // Let RHF's resolver finish first. If it rejects the cardholder fields,
    // this onInvalid callback then adds the TapPay errors instead of having
    // them overwritten by the resolver's result.
    return form.handleSubmit(submitPayment, syncHostedStatusAfterValidation)(event)
  }

  const numberInvalid = Boolean(errors.number)
  const expiryInvalid = Boolean(errors.expiry)
  const ccvInvalid = Boolean(errors.ccv)
  const phoneRegistration = form.register('phoneNumber')

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div style={payGridStyle}>
        <Field
          label={t('billing.cardholderName')}
          htmlFor={nameId}
          error={errors.name?.message ?? null}
        >
          <Input
            id={nameId}
            type="text"
            autoComplete="cc-name"
            disabled={disabled || isSubmitting}
            {...form.register('name')}
          />
        </Field>
        <Field
          label={t('billing.cardholderEmail')}
          htmlFor={emailId}
          error={errors.email?.message ?? null}
        >
          <Input
            id={emailId}
            type="email"
            autoComplete="email"
            disabled={disabled || isSubmitting}
            {...form.register('email')}
          />
        </Field>
        {emailHint ? (
          <p className="field-hint" data-testid="card-email-hint">
            {emailHint}
          </p>
        ) : null}
        <Field
          label={t('billing.cardholderPhone')}
          htmlFor={phoneId}
          error={errors.phoneNumber?.message ?? null}
        >
          <Input
            id={phoneId}
            className="num"
            type="tel"
            autoComplete="tel"
            inputMode="numeric"
            maxLength={10}
            placeholder={t('billing.cardholderPhonePlaceholder')}
            disabled={disabled || isSubmitting}
            {...phoneRegistration}
            onChange={(event) => {
              event.target.value = event.target.value.replace(/\D/g, '').slice(0, 10)
              phoneRegistration.onChange(event)
            }}
          />
        </Field>
        <Field label={t('billing.cardNumber')} error={numberInvalid ? hostedError.number : null}>
          <HostedCardInput
            id={numberId}
            label={t('billing.cardNumber')}
            invalid={numberInvalid}
            containerRef={numberRef}
            testId="tappay-card-number"
          />
        </Field>
        <div style={twoColStyle}>
          <Field label={t('billing.expiryDate')} error={expiryInvalid ? hostedError.expiry : null}>
            <HostedCardInput
              id={expiryId}
              label={t('billing.expiryDate')}
              invalid={expiryInvalid}
              containerRef={expiryRef}
              testId="tappay-card-expiry"
            />
          </Field>
          <Field label={t('billing.ccv')} error={ccvInvalid ? hostedError.ccv : null}>
            <HostedCardInput
              id={ccvId}
              label={t('billing.ccv')}
              invalid={ccvInvalid}
              containerRef={ccvRef}
              testId="tappay-card-ccv"
            />
          </Field>
        </div>
      </div>

      <Button
        type="submit"
        className="mt-[var(--gap-md)] w-full justify-center"
        disabled={disabled || isSubmitting || !ready}
        aria-busy={isSubmitting}
        data-testid="tappay-submit"
      >
        {isSubmitting ? submittingLabel : submitLabel}
      </Button>

      {errors.root?.card?.message && (
        <p className="alert show" role="alert" data-testid="tappay-error">
          {errors.root.card.message}
        </p>
      )}
    </form>
  )
}
